// xG Timeline — types + selection (Phase 3, D3 SVG area/line chart).
//
// One team's non-penalty goals-minus-xG (npG − npxG) over recent games: either
// the raw per-game delta, or a 5-game rolling average. Above the zero line =
// overperforming (blue), below = underperforming (red). The chart geometry + D3
// animation live in the component; the pure part is windowing the games and
// (for rolling) computing the moving average + keeping the source games for
// tooltips.

/** One game's non-penalty goals + non-penalty xG. */
export interface XgTimelineGame {
  npG: number;
  npxG: number;
}
/** xG-timeline games keyed by full team name. */
export type XgTimelineData = Record<string, XgTimelineGame[]>;

/** Shaped xG timeline: the plotted values + the games behind them + the mode. */
export interface XgTimelineShape {
  /** The plotted series — per-game delta, or the rolling average. */
  displayData: number[];
  /** The game behind each plotted point (for tooltips); same length as displayData. */
  sourceGames: XgTimelineGame[];
  isRolling: boolean;
}

/** Rolling-average window (games), matching the legacy renderer. */
const WINDOW = 5;

/**
 * Window a team's games and build the plotted series. Returns null when the
 * team has fewer than 5 games (the legacy "No data" guard).
 *
 * Rolling mode pulls `N + WINDOW - 1` games so the first plotted point already
 * has a full 5-game window, then plots the moving average; `sourceGames` is the
 * trailing game of each window. Actual mode plots the last N per-game deltas.
 */
export function selectXgTimeline(
  data: XgTimelineData,
  team: string,
  timeline: string | null | undefined,
  view: string | null | undefined
): XgTimelineShape | null {
  const allGames = data[team] ?? [];
  if (allGames.length < 5) return null;

  const N =
    timeline === "Season" ? allGames.length : timeline === "10" ? 10 : 5;
  const isRolling = (view || "Rolling") === "Rolling";

  if (isRolling) {
    const srcCount = Math.min(allGames.length, N + WINDOW - 1);
    const games = allGames.slice(-srcCount);
    const deltas = games.map((g) => g.npG - g.npxG);
    const rolling: number[] = [];
    for (let i = WINDOW - 1; i < deltas.length; i++) {
      let sum = 0;
      for (let j = 0; j < WINDOW; j++) sum += deltas[i - j]!;
      rolling.push(sum / WINDOW);
    }
    return {
      displayData: rolling,
      sourceGames: games.slice(WINDOW - 1),
      isRolling,
    };
  }

  const games = allGames.slice(-N);
  return {
    displayData: games.map((g) => g.npG - g.npxG),
    sourceGames: games,
    isRolling,
  };
}

/**
 * Tooltip text for the dot at index `i`. Rolling mode shows the rolling average
 * + the trailing game's actual delta; actual mode shows npG/npxG + the delta.
 * Matches the legacy renderer's tooltip format exactly.
 */
export function xgtTooltip(shape: XgTimelineShape, i: number): string {
  const g = shape.sourceGames[i];
  if (!g) return "";
  const diff = g.npG - g.npxG;
  const sign = diff >= 0 ? "+" : "";
  if (shape.isRolling) {
    const d = shape.displayData[i] ?? 0;
    return `Rolling avg: ${d.toFixed(2)}\nActual: ${sign}${diff.toFixed(2)}`;
  }
  return `npG: ${g.npG} | npxG: ${g.npxG.toFixed(2)}\nDiff: ${sign}${diff.toFixed(2)}`;
}
